import React from 'react';
import { PeriodType } from '@shopify-multiseller/graphql/partner/types';
import { Page } from '../../components';
import { AdminUI } from '../../ui/AdminUI';
import { useAuth } from '../../hooks/useAuth';
import DashboardChart from './DashboardChart';
import { DashboardActions } from './DashboardActions';
import { DashboardStatistics } from './DashboardStatistics';
import { toDateRange } from './dateRange';
import { RecentOrders } from './RecentOrders';

/** Greets by time of day — "Here's how your store is performing" at 2am reads as a machine. */
const greeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export const Dashboard: React.FC = () => {
  const auth = useAuth();
  const firstName = auth?.user?.seller?.firstName;
  const storeName = auth?.user?.seller?.displayName;

  // Fixed at thirty days, and not adjustable here. This screen is a summary:
  // it answers "how am I doing" at a glance, and everything on it is scoped to
  // one window so the numbers can be read against each other without checking
  // what range each was drawn from. Choosing ranges, comparing periods and
  // breaking figures down belong on a reporting screen of their own, which is
  // deliberately not this one.
  const period = PeriodType.Last_30Days;
  const dateRange = toDateRange(period, undefined);

  return (
    <AdminUI>
      <Page>
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {firstName ? `${greeting()}, ${firstName}` : greeting()}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {storeName
                ? `Here's how ${storeName} is performing over the last 30 days.`
                : "Here's how your store is performing over the last 30 days."}
            </p>
          </div>
        </div>

        {/* What needs doing comes before what happened: the alerts are
            actionable and time-limited, the numbers are neither. */}
        <DashboardActions />

        <div className="space-y-5">
          <DashboardStatistics dateRange={dateRange} />
          <div className="grid gap-5 lg:grid-cols-[2fr_1fr] lg:items-start">
            <DashboardChart dateRange={dateRange} period={period} />
            <RecentOrders />
          </div>
        </div>
      </Page>
    </AdminUI>
  );
};
